import {
  Address,
  ChainKey,
  ChecksumAddress,
  DEFAULT_PROTOCOL_KEY,
  getChainKeyFromProtocol,
  LINKS,
  makeAddressSync,
  ProtocolKey,
  RUNTIME_PROTOCOL_INSTANCES,
  WalletKey,
  WalletOption,
} from '@silo/types';
import {
  AccountDetailsTransaction,
  DesktopNav as BaseDesktopNav,
  ConnectWalletBus,
  DEPRECATED_Popover,
  DEPRECATED_PopoverButton,
  DEPRECATED_PopoverPanel,
  DiscordIcon,
  FlippingChevron,
  ProtocolMenu,
  TelegramIcon,
  UnstyledLink,
  useDeprecatedPopoverContext,
  Wallet,
} from '@silo/ui';
import {
  getWalletKeyFromSupportedWallet,
  getWalletOptions,
  SupportedWallet,
  useSiloWeb3,
  useWalletInfo,
} from '@silo/web3';
import { PATH } from 'app/hooks/useRoute';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useRef } from 'react';
import { connectWallet, mapTransactions } from './utils';
import { DesktopRewardsTrigger } from './desktop-rewards-trigger';

export function DesktopNav(): JSX.Element {
  const { account, protocolInstance, setProtocolInstance, isAttemptingChainUpdate, activeWallet } = useSiloWeb3();
  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;

  return (
    <BaseDesktopNav
      logoLink={
        <UnstyledLink href={PATH.DASHBOARD}>
          <BaseDesktopNav.LogoLinkContent />
        </UnstyledLink>
      }
      links={
        <BaseDesktopNav.NavLinkContainer>
          <DesktopNavLink path={PATH.DASHBOARD}>Dashboard</DesktopNavLink>

          <DesktopNavLink path={LINKS.DISCORD} external>
            <DiscordIcon />
            Discord
          </DesktopNavLink>

          <DesktopNavLink path={LINKS.TELEGRAM} external>
            <TelegramIcon />
            Telegram
          </DesktopNavLink>
        </BaseDesktopNav.NavLinkContainer>
      }
      protocolMenu={
        <ProtocolMenu
          protocolKey={protocolKey}
          runtimeProtocolKeys={RUNTIME_PROTOCOL_INSTANCES}
          setProtocol={setProtocolInstance}
          isAttemptingChainUpdate={isAttemptingChainUpdate}
          showProtocolLabel={!account}
        />
      }
      wallet={
        account && activeWallet ? (
          <ConnectedWallet protocolKey={protocolKey} account={account} wallet={activeWallet} />
        ) : (
          <DisconnectedWallet />
        )
      }
      rewards={<DesktopRewardsTrigger protocolKey={protocolKey} />}
    />
  );
}

interface DesktopNavLinkProps {
  path: string;
  external?: boolean;
  children?: ReactNode;
}

function DesktopNavLink({ path, children, external }: DesktopNavLinkProps): JSX.Element {
  const router = useRouter();
  const isActive = path === router.route;

  return (
    <UnstyledLink className={BaseDesktopNav.navLink({ isActive })} href={path} external={external}>
      {children}
    </UnstyledLink>
  );
}

function DisconnectedWallet(): JSX.Element {
  const walletOptions = getWalletOptions();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const unsubscribe = ConnectWalletBus.subscribe(() => {
      buttonRef.current?.click();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <DEPRECATED_Popover className="w-fit">
      <DEPRECATED_PopoverButton ref={buttonRef} data-cy="wallet-info-button" className={Wallet.disconnectedTrigger()}>
        <Wallet.DisconnectedTriggerContent />

        <PopoverChevron />
      </DEPRECATED_PopoverButton>

      <DEPRECATED_PopoverPanel className={Wallet.popoverContent()}>
        <Wallet.DisconnectedFlow connectWallet={connectWallet} walletOptions={walletOptions} />
      </DEPRECATED_PopoverPanel>
    </DEPRECATED_Popover>
  );
}

interface ConnectedWalletProps {
  protocolKey: ProtocolKey;
  account: ChecksumAddress;
  wallet: SupportedWallet;
}

function ConnectedWallet({ protocolKey, account: accountLegacy, wallet }: ConnectedWalletProps): JSX.Element {
  const siloWeb3 = useSiloWeb3();
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const unsubscribe = ConnectWalletBus.subscribe(() => {
      buttonRef.current?.click();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const ensName = siloWeb3.ensName.data ?? undefined;
  const { isImpersonating, disconnect } = siloWeb3;

  const chainKey = getChainKeyFromProtocol(protocolKey);
  const account = makeAddressSync(accountLegacy);
  const walletInfo = useWalletInfo();
  const { walletInfoState, clearTransactions } = walletInfo;
  const recentTransactions = mapTransactions(walletInfo.recentTransactions);
  const activeWalletKey = getWalletKeyFromSupportedWallet(wallet);
  const walletOptions = getWalletOptions();

  return (
    <DEPRECATED_Popover className="w-fit">
      <DEPRECATED_PopoverButton data-cy="wallet-info-button" className={Wallet.connectedTrigger()}>
        <Wallet.ConnectedTriggerContent
          account={account}
          state={walletInfoState}
          walletKey={activeWalletKey}
          ensName={ensName}
          isImpersonating={isImpersonating}
          chevron={<PopoverChevron />}
        />
      </DEPRECATED_PopoverButton>

      <DEPRECATED_PopoverPanel className={Wallet.popoverContent()}>
        <ConnectedPopoverContent
          isImpersonating={isImpersonating}
          account={account}
          ensName={ensName}
          disconnect={disconnect}
          walletOptions={walletOptions}
          activeWalletKey={activeWalletKey}
          chainKey={chainKey}
          recentTransactions={recentTransactions}
          clearTransactions={clearTransactions}
        />
      </DEPRECATED_PopoverPanel>
    </DEPRECATED_Popover>
  );
}

interface ConnectedPopoverContentProps {
  isImpersonating: boolean;
  account: Address;
  ensName: string | undefined;
  disconnect: () => void;
  walletOptions: WalletOption[];
  activeWalletKey: WalletKey;
  chainKey: ChainKey;
  recentTransactions: AccountDetailsTransaction[];
  clearTransactions: () => void;
}

function ConnectedPopoverContent({
  isImpersonating,
  account,
  ensName,
  disconnect,
  walletOptions,
  activeWalletKey,
  chainKey,
  recentTransactions,
  clearTransactions,
}: ConnectedPopoverContentProps): JSX.Element {
  const { closePopover } = useDeprecatedPopoverContext();

  return (
    <Wallet.ConnectedFlow
      isImpersonating={isImpersonating}
      account={account}
      ensName={ensName}
      disconnect={disconnect}
      connectWallet={connectWallet}
      walletOptions={walletOptions}
      activeWalletKey={activeWalletKey}
      chainKey={chainKey}
      recentTransactions={recentTransactions}
      clearTransactions={clearTransactions}
      onConnect={closePopover}
    />
  );
}

function PopoverChevron(): JSX.Element {
  const { isOpen } = useDeprecatedPopoverContext();

  return <FlippingChevron isOpen={isOpen} />;
}
